import type { LevelDef } from '../content/levelSchema';
import { CAMPAIGN_LEVELS } from '../content/levels/campaign';
import { bossFor } from '../gen/boss';
import { campaignLevelId, rareItemFor } from '../meta/economy';
import { STAR, type Save } from '../meta/save/schema';
import { needsMowing } from '../sim/grid';

/**
 * Rare finds that get away (docs/GDD.md §8). A lawn's own find is there until an attempt at the
 * lawn ends without a clean clear; then it is gone from that lawn for good and wanders on: it turns
 * up on the next campaign lawn after it that is still to clear, has no find of its own and isn't a
 * boss. Escape the same way there and it moves on again, so every find stays obtainable.
 */
export interface RareFind {
  item: string;
  /** Wandered in from another lawn (not this lawn's own). */
  wandering: boolean;
}

const hasOwnFind = (level: LevelDef) => (level.sim.pickups ?? []).some((p) => p.kind === 'rare');
const cleared = (save: Readonly<Save>, n: number) =>
  ((save.levels[campaignLevelId(n)]?.stars ?? 0) & STAR.clear) !== 0;

/** Which lawn each wandering find is on now (campaign lawn numbers), in wander order. */
function wanderingHosts(save: Readonly<Save>): Map<string, number> {
  const hosts = new Map<string, number>();
  const taken = new Set<number>();
  for (const w of save.rareWandering) {
    for (let n = w.from + 1; n <= CAMPAIGN_LEVELS.length; n++) {
      const level = CAMPAIGN_LEVELS[n - 1]!;
      const ownStillThere = hasOwnFind(level) && !save.rareEscaped.includes(level.id);
      if (cleared(save, n) || bossFor(n) || ownStillThere || taken.has(n)) continue;
      hosts.set(w.item, n);
      taken.add(n);
      break;
    }
  }
  return hosts;
}

/** The rare find waiting on campaign lawn `n` now, or null. */
export function rareFindOn(save: Readonly<Save>, n: number): RareFind | null {
  const level = CAMPAIGN_LEVELS[n - 1];
  if (!level) return null;
  if (hasOwnFind(level) && !save.rareEscaped.includes(level.id)) {
    return { item: rareItemFor(level.id), wandering: false };
  }
  for (const [item, host] of wanderingHosts(save)) {
    if (host === n) return { item, wandering: true };
  }
  return null;
}

/**
 * Campaign lawn `n` as it is played now: without its own rare find if that got away, with a
 * wandering one placed on the open grass farthest from the start.
 */
export function lawnWithFinds(level: LevelDef, n: number, save: Readonly<Save>): LevelDef {
  const find = rareFindOn(save, n);
  const others = (level.sim.pickups ?? []).filter((p) => p.kind !== 'rare');
  if (!find) return { ...level, sim: { ...level.sim, pickups: others } };
  if (!find.wandering) return level;
  const { width, start } = level.sim;
  const taken = new Set([
    ...others.map((p) => p.y * width + p.x),
    ...(level.sim.critters ?? []).map((c) => c.y * width + c.x),
    start.y * width + start.x,
  ]);
  let spot = -1;
  let far = -1;
  level.sim.tiles.forEach((t, i) => {
    if (!needsMowing(t) || taken.has(i)) return;
    const d = Math.abs((i % width) - start.x) + Math.abs(Math.floor(i / width) - start.y);
    if (d > far) {
      far = d;
      spot = i;
    }
  });
  if (spot < 0) return { ...level, sim: { ...level.sim, pickups: others } };
  const rare = { kind: 'rare' as const, x: spot % width, y: Math.floor(spot / width) };
  return { ...level, sim: { ...level.sim, pickups: [...others, rare] } };
}
